// Renewal (rebaseline) helpers for PermissionRequest.jsx. Pure functions, no React.
// The server (validator storeResults) re-checks everything here at submit.

const byExpiryThenNewest = (a, b) => {
    // Has accessEndDate first, then accessEndDate ascending (soonest expiry first)
    const aExpiry = a.accessEndDate || '';
    const bExpiry = b.accessEndDate || '';
    if (aExpiry && !bExpiry) return -1;
    if (!aExpiry && bExpiry) return 1;
    if (aExpiry && bExpiry) return aExpiry.localeCompare(bExpiry);
    // Then by createdAt descending (most recent first)
    return (b.createdAt || '').localeCompare(a.createdAt || '');
};

// T321 requests can't be renewed here (the server only renews T178 originals). getMyRequests
// doesn't project `tenant`, so the PAM-T321- id prefix is the signal; `tenant` is honoured if present.
const isT321 = (r) => r.requestId.startsWith('PAM-T321-') || (!!r.tenant && r.tenant !== 'T178');

// #9: the picker lists only APPROVED T178 requests that have not been renewed yet.
export const renewalPickerFilter = (requests) => (requests || [])
    .filter(r => r.requestId && !r.requestId.startsWith('JOB-')
        && !isT321(r)
        && r.status === 'APPROVED'
        && !r.renewalRequestId
        && !r.supersededBy
        && r.expiryState !== 'SUPERSEDED_AT_BACKFILL')
    .sort(byExpiryThenNewest);

// Same refusals (and texts) as the server. null = renewable.
export const renewalRefusal = (data) => {
    const d = data || {};
    if (isT321({ requestId: String(d.requestId || ''), tenant: d.tenant })) return 'Only T178 requests can be renewed here.';
    if (d.expiryState === 'SUPERSEDED_AT_BACKFILL') {
        const cand = d.supersededByCandidate || '';
        return `${d.requestId} looks renewed by ${cand}; renew ${cand} instead.`;
    }
    if (d.status === 'REBASELINED' || d.renewalRequestId || d.supersededBy) return 'This request was already renewed.';
    if (d.status !== 'APPROVED') return 'Only an approved request can be renewed.';
    return null;
};

// One id per submit click, shared by every risk-type group of that submit.
export const newSubmissionId = () => {
    const c = typeof crypto !== 'undefined' ? crypto : null;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    const bytes = new Uint8Array(16);
    c.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
};

// Viewer link on the site the request is submitted from (prod → prod, pre-prod → pre-prod), like
// viewerUrlFor in T321Request.jsx. The server only accepts the prod / pre-prod link and falls back to prod.
export const viewerUrlFor = (requestId, origin = window.location.origin) =>
    `${origin}/viewer?id=${encodeURIComponent(requestId || '')}`;

// #1: renewal fields of the storeResults payload. previous* are kept unchanged.
export const renewalSubmitFields = ({ originalRequestId, previousApprovalId, renewalSubmissionId }) => ({
    previousRequestId: originalRequestId,
    previousApprovalId: previousApprovalId,
    previousViewerUrl: viewerUrlFor(originalRequestId),
    originalRequestId,
    renewalSubmissionId,
});

// #5: what step 1 shows. A legacy 'skip' value behaves like "no original yet".
export const renewalStep1View = (requestType, originalRequestId) => {
    const id = originalRequestId || '';
    const loaded = requestType === 'renewal' && !!id && id !== 'skip';
    const showPicker = requestType === 'renewal' && (!id || id === 'skip');
    return { loaded, showTypeCards: !loaded, showPicker, showSkip: false, showChip: loaded };
};
